import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { StorefrontPreview } from "@/components/storefront/StorefrontPreview";
import { Store } from "@/models/storeModel";

interface Testimonial {
  id: string;
  name: string;
  review: string;
  rating: number;
  role?: string;
}

interface StorefrontContent {
  hero: {
    title: string;
    subtitle: string;
    buttonText: string;
    buttonAction: string;
    backgroundImage?: string;
  };
  colors: {
    background: string;
    primary: string;
    secondary: string;
    accent: string;
    text: string;
  };
  testimonials?: Testimonial[];
}

const defaultContent: StorefrontContent = {
  hero: {
    title: "Welcome to Our Store",
    subtitle: "Discover amazing products and services",
    buttonText: "Explore Now",
    buttonAction: "#menu",
  },
  colors: {
    background: "#ffffff",
    primary: "#6366F1",
    secondary: "#EC4899",
    accent: "#8B5CF6",
    text: "#2C3E50",
  },
  testimonials: [],
};

export default function PublicStorefrontPage() {
  const { slug } = useParams();
  const [store, setStore] = useState<Store | null>(null);
  const [content, setContent] = useState<StorefrontContent>(defaultContent);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchStoreBySlug = async () => {
      if (!slug) return;

      try {
        setLoading(true);

        const { data, error } = await supabase
          .from("stores")
          .select("*")
          .eq("slug", slug)
          .eq("is_active", true)
          .single();

        if (error) {
          console.error("Error fetching store:", error);
          setError("Store not found");
          return;
        }

        if (data) {
          setStore({
            ...data,
            business_hours:
              typeof data.business_hours === "string"
                ? JSON.parse(data.business_hours)
                : data.business_hours,
          });
          if (data.storefront_content) {
            setContent(JSON.parse(data.storefront_content as string));
          }
        } else {
          setError("Store not found");
        }
      } catch (err) {
        console.error("Failed to fetch store:", err);
        setError("Failed to load store");
      } finally {
        setLoading(false);
      }
    };

    fetchStoreBySlug();
  }, [slug]);

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-gray-900 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading storefront...</p>
        </div>
      </div>
    );
  }
  if (!store) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900">
            Store Not Found....
          </h1>
          <p className="text-gray-600">
            {error || "The requested store could not be found."}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <StorefrontPreview
        store={store}
        content={content}
        isEditing={false}
        isPublished={true}
        onContentChange={() => {}} // No editing allowed in public view
      />
    </div>
  );
}
